import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URLS, TIMEOUTS } from '../config/apiConfig';

// Base API configuration for admin panel
const API_BASE_URL = API_URLS.ADMIN_BASE;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUTS.DEFAULT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token') || Cookies.get('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only redirect if we're not already on the login page
      const currentPath = window.location.pathname;
      if (currentPath !== '/glam-admin/login') {
        // Token expired or invalid - clear auth data and redirect
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        Cookies.remove('access_token');
        Cookies.remove('user');
        // Add a small delay to prevent redirect loops during component mounting
        setTimeout(() => {
          window.location.href = '/glam-admin/login';
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  setupPassword: (token, password) => api.post('/merchants/setup-password', { token, password }),
};

// Users API
export const usersAPI = {
  getAll: (params = {}) => api.get('/users/', { params }),
  getAllForPermissions: (params = {}) => api.get('/users/for-permissions', { params }), // New method for permissions management
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Roles API
export const rolesAPI = {
  getAll: (params = {}) => api.get('/roles/', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getDropdown: () => api.get('/roles/dropdown'), // Get all active roles for dropdown selection
};

// Departments API
export const departmentsAPI = {
  getAll: (params = {}) => api.get('/departments/', { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments/', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Companies API
export const companiesAPI = {
  getAll: (params = {}) => api.get('/companies/', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies/', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getDropdown: () => api.get('/companies/dropdown'), // Get all active companies for dropdown selection
};

// Modules API
export const modulesAPI = {
  getAll: (params = {}) => api.get('/modules/', { params }),
  getModules: (params) => api.get('/modules/', { params }),
  getById: (id) => api.get(`/modules/${id}`),
  create: (data) => api.post('/modules/', data),
  update: (id, data) => api.put(`/modules/${id}`, data),
  delete: (id) => api.delete(`/modules/${id}`),
};

// Module Actions API
export const moduleActionsAPI = {
  getModuleActions: (moduleId) => api.get(`/module-actions/module/${moduleId}/actions`),
  createAction: (moduleId, actionData) => api.post(`/module-actions/module/${moduleId}/actions`, actionData),
  updateAction: (actionId, actionData) => api.put(`/module-actions/action/${actionId}`, actionData),
  deleteAction: (actionId, forceDelete = false) =>
    api.delete(`/module-actions/action/${actionId}${forceDelete ? '?force=true' : ''}`),
  createBulkActions: (moduleId, actionsData) => api.post('/module-actions/actions/bulk', {
    module_id: moduleId,
    actions: actionsData,
  }),
};

// Permissions API
export const permissionsAPI = {
  getUserPermissions: (userId) => userId ? api.get(`/permissions/user/${userId}`) : api.get('/permissions/user'),
  getUserPermissionsForManagement: (userId) => api.get(`/permissions/user/${userId}/management`),
  getRolePermissions: (roleId) => api.get(`/permissions/role/${roleId}`),
  updateRolePermissions: (roleId, permissions) =>
    api.post(`/permissions/role/${roleId}`, { permissions }),
  updateUserPermissions: (userId, permissions) =>
    api.post(`/permissions/user/${userId}`, { permissions }),
  getModuleActions: () => api.get('/permissions/module-actions'),
  getUserSummary: (userId) => api.get(`/permissions/user/${userId}/summary`),
  checkSuperAdmin: () => api.get('/permissions/check-superadmin'), // Check if current user is superadmin
};

// User Roles API
export const userRolesAPI = {
  getAll: (params = {}) => api.get('/user-roles/', { params }),
  assign: (data) => api.post(`/user-roles/user/${data.user_id}/roles`, data),
  unassign: (mappingId) => api.delete(`/user-roles/user-role/${mappingId}`),
  update: (mappingId, data) => api.put(`/user-roles/user-role/${mappingId}`, data),
  getUserRoles: (userId) => api.get(`/user-roles/user/${userId}/roles`),
  checkUserRole: (userId, params = {}) => api.get(`/user-roles/user/${userId}/check-role`, { params }),
};

// Menu API
export const menuAPI = {
  getSidebar: () => api.get('/menu/sidebar'),
  getNavigation: () => api.get('/menu/navigation'),
  getPermissions: () => api.get('/menu/permissions'),
};

// Audit API
export const auditAPI = {
  getLogs: (params = {}) => api.get('/audit/logs', { params }),
  getStats: () => api.get('/audit/stats'),
  getFailures: (params = {}) => api.get('/audit/failures', { params }),
  exportLogs: (params = {}) => api.get('/audit/export', { params, responseType: 'blob' }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivitySummary: () => api.get('/dashboard/activity-summary'),
  getMerchantStats: () => api.get('/dashboard/merchant-stats'),
  getAiFailsafeStatus: () => api.get('/dashboard/ai-failsafe-status'),
};

// Merchant Management API
export const merchantManagementAPI = {
  getAll: (params = {}) => api.get('/merchants/', { params }),
  getById: (id) => api.get(`/merchants/${id}`),
  create: (data) => api.post('/merchants/', data),
  update: (id, data) => api.put(`/merchants/${id}`, data),
  delete: (id) => api.delete(`/merchants/${id}`),
  suspend: (id, reason) => api.post(`/merchants/${id}/suspend`, { reason }),
  activate: (id) => api.post(`/merchants/${id}/activate`),
  addCredits: (id, data) => api.post(`/merchants/${id}/credits`, data),
  getGenerations: (id, params = {}) => api.get(`/merchants/${id}/generations`, { params }),
  getTransactions: (id, params = {}) => api.get(`/merchants/${id}/transactions`, { params }),
  exportData: (filters = {}) => {
    const params = {
      search: filters.search || '',
      status: filters.status || ''
    };
    return api.get('/merchants/export', { params, responseType: 'blob' });
  },
  getStats: () => api.get('/merchants/stats'),
  getIndustryTypes: () => api.get('/merchants/industry-types'),
  bulkSuspend: (ids, reason) => api.post('/merchants/bulk-suspend', { ids, reason }),
  bulkActivate: (ids) => api.post('/merchants/bulk-activate', { ids }),
  bulkAddCredits: (ids, credits, reason) => api.post('/merchants/bulk-add-credits', { ids, credits, reason }),
  sendVerificationOTP: (id) => api.post(`/merchants/${id}/send-otp`),
  verifyMerchantOTP: (id, data) => api.post(`/merchants/${id}/verify-otp`, data),
  sendPasswordSetupLink: (id) => api.post(`/merchants/${id}/send-password-link`), // New method for password setup link
  resendPasswordSetupLink: (id) => api.post(`/merchants/${id}/resend-password-link`), // For resending password setup link
  getAIGenerationHistory: (params = {}) => api.get('/merchants/ai-generation-history', { params }),
  processRefund: (generationId, data) => api.post(`/merchants/ai-generations/${generationId}/refund`, data),
  getGenerationApiLogsSummary: (generationId) => api.get(`/merchants/ai-generation/${generationId}/api-logs-summary`),
  // Account verification — free tokens are granted only after approval
  getVerification: (id) => api.get(`/merchants/${id}/verification`),
  approveVerification: (id, data = {}) => api.post(`/merchants/${id}/verification/approve`, data),
  rejectVerification: (id, data) => api.post(`/merchants/${id}/verification/reject`, data),
};

// Credit System Management API
export const creditSystemAPI = {
  getPackages: (params = {}) => api.get('/credits/packages', { params }),
  createPackage: (data) => api.post('/credits/packages', data),
  updatePackage: (id, data) => api.put(`/credits/packages/${id}`, data),
  deletePackage: (id) => api.delete(`/credits/packages/${id}`),
  getTransactions: (params = {}) => api.get('/credits/transactions', { params }),
  getTransactionTypes: () => api.get('/credits/transaction-types'),
  getMerchants: () => api.get('/credits/merchants'),
  getStats: () => api.get('/credits/stats'),
  exportTransactions: (params = {}) => api.get('/credits/export', { params, responseType: 'blob' }),
  approveCreditRequest: (id, data) => api.post(`/credits/requests/${id}/approve`, data),
  rejectCreditRequest: (id, data) => api.post(`/credits/requests/${id}/reject`, data),
  getCreditRequests: (params = {}) => api.get('/credits/requests', { params }),
  getSubscriptionRequests: (params = {}) => api.get('/credits/subscription-requests', { params }),
  getCreditRequestStatuses: () => api.get('/credits/requests/statuses'),
};

// Master Data Configuration API
export const masterDataConfigAPI = {
  // Model Types
  getModelTypes: (params = {}) => api.get('/master-data/model_types', { params }),
  createModelType: (data) => api.post('/master-data/model_types', data),
  updateModelType: (id, data) => api.put(`/master-data/model_types/${id}`, data),
  deleteModelType: (id) => api.delete(`/master-data/model_types/${id}`),

  // Bust Colors
  getBustColors: (params = {}) => api.get('/master-data/bust_colors', { params }),
  createBustColor: (data) => api.post('/master-data/bust_colors', data),
  updateBustColor: (id, data) => api.put(`/master-data/bust_colors/${id}`, data),
  deleteBustColor: (id) => api.delete(`/master-data/bust_colors/${id}`),

  // Gender Options
  getGenderOptions: (params = {}) => api.get('/master-data/gender_options', { params }),
  createGenderOption: (data) => api.post('/master-data/gender_options', data),
  updateGenderOption: (id, data) => api.put(`/master-data/gender_options/${id}`, data),
  deleteGenderOption: (id) => api.delete(`/master-data/gender_options/${id}`),

  // Skin Tones
  getSkinTones: (params = {}) => api.get('/master-data/skin_tones', { params }),
  createSkinTone: (data) => api.post('/master-data/skin_tones', data),
  updateSkinTone: (id, data) => api.put(`/master-data/skin_tones/${id}`, data),
  deleteSkinTone: (id) => api.delete(`/master-data/skin_tones/${id}`),

  // Ethnicities
  getEthnicities: (params = {}) => api.get('/master-data/ethnicities', { params }),
  createEthnicity: (data) => api.post('/master-data/ethnicities', data),
  updateEthnicity: (id, data) => api.put(`/master-data/ethnicities/${id}`, data),
  deleteEthnicity: (id) => api.delete(`/master-data/ethnicities/${id}`),

  // Clothing Types
  getClothingTypes: (params = {}) => api.get('/master-data/clothing_types', { params }),
  createClothingType: (data) => api.post('/master-data/clothing_types', data),
  updateClothingType: (id, data) => api.put(`/master-data/clothing_types/${id}`, data),
  deleteClothingType: (id) => api.delete(`/master-data/clothing_types/${id}`),

  // Bust Finishes
  getBustFinishes: (params = {}) => api.get('/master-data/bust_finishes', { params }),
  createBustFinish: (data) => api.post('/master-data/bust_finishes', data),
  updateBustFinish: (id, data) => api.put(`/master-data/bust_finishes/${id}`, data),
  deleteBustFinish: (id) => api.delete(`/master-data/bust_finishes/${id}`),

  // Media Qualities
  getMediaQualities: (params = {}) => api.get('/master-data/media_qualities', { params }),
  createMediaQuality: (data) => api.post('/master-data/media_qualities', data),
  updateMediaQuality: (id, data) => api.put(`/master-data/media_qualities/${id}`, data),
  deleteMediaQuality: (id) => api.delete(`/master-data/media_qualities/${id}`),

  // Output Formats
  getOutputFormats: (params = {}) => api.get('/master-data/output_formats', { params }),
  createOutputFormat: (data) => api.post('/master-data/output_formats', data),
  updateOutputFormat: (id, data) => api.put(`/master-data/output_formats/${id}`, data),
  deleteOutputFormat: (id) => api.delete(`/master-data/output_formats/${id}`),

  // Categories
  getCategories: (params = {}) => api.get('/master-data/categories', { params }),
  createCategory: (data) => api.post('/master-data/categories', data),
  updateCategory: (id, data) => api.put(`/master-data/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/master-data/categories/${id}`),

  // Materials
  getMaterials: (params = {}) => api.get('/master-data/materials', { params }),
  createMaterial: (data) => api.post('/master-data/materials', data),
  updateMaterial: (id, data) => api.put(`/master-data/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/master-data/materials/${id}`),

  // Background Types
  getBackgroundTypes: (params = {}) => api.get('/master-data/background_types', { params }),
  createBackgroundType: (data) => api.post('/master-data/background_types', data),
  updateBackgroundType: (id, data) => api.put(`/master-data/background_types/${id}`, data),
  deleteBackgroundType: (id) => api.delete(`/master-data/background_types/${id}`),

  // Generation Statuses
  getGenerationStatuses: (params = {}) => api.get('/master-data/generation_statuses', { params }),
  createGenerationStatus: (data) => api.post('/master-data/generation_statuses', data),
  updateGenerationStatus: (id, data) => api.put(`/master-data/generation_statuses/${id}`, data),
  deleteGenerationStatus: (id) => api.delete(`/master-data/generation_statuses/${id}`),

  // Industry Types (Add this new section)
  getIndustryTypes: (params = {}) => api.get('/master-data/industry_types', { params }),
  createIndustryType: (data) => api.post('/master-data/industry_types', data),
  updateIndustryType: (id, data) => api.put(`/master-data/industry_types/${id}`, data),
  deleteIndustryType: (id) => api.delete(`/master-data/industry_types/${id}`),

  // Countries
  getCountries: () => api.get('/master-data/countries'),

  // States by Country
  getStatesByCountry: (countryId) => api.get(`/master-data/states/${countryId}`),

  // Cities by State
  getCitiesByState: (stateId) => api.get(`/master-data/cities/${stateId}`),

  // Brand Logos
  getBrandLogos: (params = {}) => api.get('/master-data/brand_logos', { params }),
  createBrandLogo: (data) => api.post('/master-data/brand_logos', data),
  updateBrandLogo: (id, data) => api.put(`/master-data/brand_logos/${id}`, data),
  deleteBrandLogo: (id) => api.delete(`/master-data/brand_logos/${id}`),
  uploadBrandLogo: (formData) => api.post('/master-data/brand-logos/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  }),
  uploadBustFinishPreview: (formData) => api.post('/master-data/bust-finishes/upload-preview', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  }),

  // Preview Image Upload
  uploadPreviewImage: (formData) => api.post('/master-data/preview-images/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  }),

  // Credit Settings
  getCreditSettings: (params = {}) => api.get('/master-data/credit_settings', { params }),
  createCreditSetting: (data) => api.post('/master-data/credit_settings', data),
  updateCreditSetting: (id, data) => api.put(`/master-data/credit_settings/${id}`, data),
  deleteCreditSetting: (id) => api.delete(`/master-data/credit_settings/${id}`),
  getLatestCreditSetting: () => api.get('/master-data/credit_settings/latest'),

  // Category Model Mappings
  getCategoryModelMappings: (params = {}) => api.get('/master-data/category_model_mappings', { params }),
  createCategoryModelMapping: (data) => api.post('/master-data/category_model_mappings', data),
  updateCategoryModelMapping: (id, data) => api.put(`/master-data/category_model_mappings/${id}`, data),
  deleteCategoryModelMapping: (id) => api.delete(`/master-data/category_model_mappings/${id}`),

  // Category Mappings (new comprehensive mapping system)
  getCategoryMappings: (params = {}) => api.get('/master-data/category-mappings', { params }),
  createCategoryMapping: (data) => api.post('/master-data/category-mappings', data),
  updateCategoryMapping: (id, data) => api.put(`/master-data/category-mappings/${id}`, data),
  deleteCategoryMapping: (id) => api.delete(`/master-data/category-mappings/${id}`),
  reactivateCategoryMapping: (id) => api.put(`/master-data/category-mappings/${id}/reactivate`),

  // Category Gender Mappings
  getCategoryGenderMappings: (params = {}) => api.get('/master-data/category-gender-mappings', { params }),
  createCategoryGenderMapping: (data) => api.post('/master-data/category-gender-mappings', data),
  updateCategoryGenderMapping: (id, data) => api.put(`/master-data/category-gender-mappings/${id}`, data),
  deleteCategoryGenderMapping: (id) => api.delete(`/master-data/category-gender-mappings/${id}`),


  // Bust Types
  getBustTypes: (params = {}) => api.get('/master-data/bust_types', { params }),
  createBustType: (data) => api.post('/master-data/bust_types', data),
  updateBustType: (id, data) => api.put(`/master-data/bust_types/${id}`, data),
  deleteBustType: (id) => api.delete(`/master-data/bust_types/${id}`),

  // Themes
  getThemes: (params = {}) => api.get('/master-data/themes', { params }),
  createTheme: (data) => api.post('/master-data/themes', data),
  updateTheme: (id, data) => api.put(`/master-data/themes/${id}`, data),
  deleteTheme: (id) => api.delete(`/master-data/themes/${id}`),

  // System Settings
  getSystemSettings: () => api.get('/master-data/settings'),
  updateSystemSetting: (key, value) => api.put(`/master-data/settings/${key}`, { value }),
  resetToDefaults: () => api.post('/master-data/settings/reset'),

  // Get public system settings (for frontend display)
  getPublicSystemSettings: () => api.get('/master-data/system-settings/public'),

  // Get public starter package information (for registration flow)
  getPublicStarterPackage: () => api.get('/master-data/starter-package/public'),

  // Initialize System Settings
  initialize: () => api.post('/master-data/initialize'),

  // Restricted Email Domains
  getRestrictedDomains: (params = {}) => api.get('/master-data/restricted_email_domains', { params }),
  createRestrictedDomain: (data) => api.post('/master-data/restricted_email_domains', data),
  updateRestrictedDomain: (id, data) => api.put(`/master-data/restricted_email_domains/${id}`, data),
  deleteRestrictedDomain: (id) => api.delete(`/master-data/restricted_email_domains/${id}`),
};


// Middleware Data API
export const middlewareDataAPI = {
  // Model Registry
  getModelRegistry: (params = {}) => api.get('/middleware-data/model-registry', { params }),
  createModelRegistry: (data) => api.post('/middleware-data/model-registry', data),
  updateModelRegistry: (id, data) => api.put(`/middleware-data/model-registry/${id}`, data),
  deleteModelRegistry: (id) => api.delete(`/middleware-data/model-registry/${id}`),

  // Themes
  getThemes: (params = {}) => api.get('/master-data/themes', { params }),
  createTheme: (data) => api.post('/master-data/themes', data),
  updateTheme: (id, data) => api.put(`/master-data/themes/${id}`, data),
  deleteTheme: (id) => api.delete(`/master-data/themes/${id}`),

  // Prompt Registry
  getPromptRegistry: (params = {}) => api.get('/middleware-data/prompt-registry', { params }),
  createPromptRegistry: (data) => api.post('/middleware-data/prompt-registry', data),
  updatePromptRegistry: (id, data) => api.put(`/middleware-data/prompt-registry/${id}`, data),
  deletePromptRegistry: (id) => api.delete(`/middleware-data/prompt-registry/${id}`),

  // Categories, Model Types, Materials, Gender Options, Skin Tones, Background Types
  getCategories: () => api.get('/middleware-data/categories'),
  getModelTypes: (categoryId = null) => {
    const params = categoryId ? { category_id: categoryId } : {};
    return api.get('/middleware-data/model-types', { params });
  },

  getGenderOptions: () => api.get('/middleware-data/gender-options'),
  getSkinTones: () => api.get('/middleware-data/skin-tones'),
  getBackgroundTypes: () => api.get('/middleware-data/background-types'),
  getOutputFormats: () => api.get('/middleware-data/output-formats'),

  getModelRegistryDropdown: () => api.get('/middleware-data/model-registry-dropdown'),

  // Soft delete management endpoints
  getDeletedModelRegistry: () => api.get('/middleware-data/model-registry/deleted'),
  restoreModelRegistry: (id) => api.put(`/middleware-data/model-registry/${id}/restore`),
  getDeletedPromptRegistry: () => api.get('/middleware-data/prompt-registry/deleted'),
  restorePromptRegistry: (id) => api.put(`/middleware-data/prompt-registry/${id}/restore`),

  // Prompt parsing
  parseProcessedPrompt: (data) => api.post('/middleware-data/parse-prompt', data),

  // Prompt registry options
  getPromptRegistryOptions: () => api.get('/middleware-data/prompt-registry-options'),
};

// Add-on Services API
export const modelFaceAPI = {
  // Model Face Images
  getImages: (params = {}) => api.get('/model-face/images', { params }),
  bulkUploadExcel: (data) => {
    return api.post('/model-face/images/bulk-upload-excel', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  downloadExcelTemplate: () => api.get('/model-face/images/bulk-upload-template', { responseType: 'blob' }),
  createImage: (data) => {
    if (data instanceof FormData) {
      return api.post('/model-face/images', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/model-face/images', data);
  },
  updateImage: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/model-face/images/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/model-face/images/${id}`, data);
  },
  deleteImage: (id) => api.delete(`/model-face/images/${id}`),
  getImageUsageSummary: (id) => api.get(`/model-face/images/${id}/usage-summary`),

  // Ethnicities Dropdown
  getEthnicities: () => api.get('/model-face/ethnicities'),

  // User Assignments
  getUserAssignments: (params = {}) => api.get('/model-face/user-assignments', { params }),
  createUserAssignment: (data) => api.post('/model-face/user-assignments', data),
  bulkAssignUsers: (data) => api.post('/model-face/user-assignments/bulk', data),
  deleteUserAssignment: (id) => api.delete(`/model-face/user-assignments/${id}`),

  // User Config
  getUserConfig: (userId) => api.get(`/model-face/user-config/${userId}`),
  updateUserConfig: (userId, data) => api.put(`/model-face/user-config/${userId}`, data),
  bulkUpdateUserConfig: (data) => api.put('/model-face/user-config/bulk', data),

  // Enabled Users
  getEnabledUsers: (params = {}) => api.get('/model-face/enabled-users', { params }),

  // User Search & balance (for addon assignment confirmation)
  searchUsers: (query) => api.get('/model-face/users/search', { params: { q: query } }),
  getUserCreditsBalance: (userId) => api.get(`/model-face/users/${userId}/credits-balance`),

  // Audit trail (ai_model_addon_logs)
  getAuditLogs: (params = {}) => api.get('/model-face/audit-logs', { params }),

  // Google Drive import (admin; 501 until backend configured)
  importModelImagesFromGoogleDrive: (data) => api.post('/model-face/images/import-google-drive', data),

  // Master Data
  getAddonServices: () => api.get('/model-face/addon-services'),
  createAddonService: (data) => api.post('/model-face/addon-services', data),
  updateAddonService: (id, data) => api.put(`/model-face/addon-services/${id}`, data),
  deleteAddonService: (id) => api.delete(`/model-face/addon-services/${id}`),
  getThemes: (params) => api.get('/master-data/themes', { params }),
  createTheme: (data) => api.post('/master-data/themes', data),
  updateTheme: (id, data) => api.put(`/master-data/themes/${id}`, data),
  deleteTheme: (id) => api.delete(`/master-data/themes/${id}`),
};

// Admin Notification API (for admin panel)
export const adminNotificationAPI = {
  sendTopicNotification: (data) => api.post('/notifications/send-topic', data),
};

// Topic Notification History API
export const topicNotificationHistoryAPI = {
  getTopicNotificationHistory: (params = {}) => api.get('/topic-notifications/', { params }),
  getTopicNotificationById: (topicId) => api.get(`/topic-notifications/${topicId}`),
  getTopicNotificationsWithBroadcast: (params = {}) => api.get('/topic-notifications/broadcast', { params }),
  getTopicNotificationStats: () => api.get('/topic-notifications/stats'),
  deleteTopicNotification: (topicId) => api.delete(`/topic-notifications/${topicId}`),
};

// Merchant Notification API (for merchant functionality)
export const merchantNotificationAPI = {
  sendSingleNotification: (data) => api.post('/notifications/send-single', data),
  sendBulkNotification: (data) => api.post('/notifications/send-bulk', data),
  sendAnnouncement: (data) => api.post('/notifications/send-announcement', data),
  subscribeToTopic: (data) => api.post('/notifications/subscribe-topic', data),
  unsubscribeFromTopic: (data) => api.post('/notifications/unsubscribe-topic', data),
};

// Referral Code Management API (for admin panel)
export const referralCodeManagementAPI = {
  // Get referral code statistics
  getStats: () => api.get('/referral-codes/statistics'),

  // Get all referral codes with pagination and filters
  getAll: (params = {}) => api.get('/referral-codes/codes', { params }),

  // Get referral code by ID
  getById: (id) => api.get(`/referral-codes/codes/${id}`),

  // Assign referral code to merchant
  assignToMerchant: (codeId, merchantId) => api.post(`/referral-codes/codes/${codeId}/assign`, { merchant_id: merchantId }),

  // Release referral code from merchant
  releaseFromMerchant: (codeId) => api.post(`/referral-codes/codes/${codeId}/release`),

  // Get referral codes for specific merchant
  getByMerchant: (merchantId) => api.get(`/referral-codes/merchants/${merchantId}/referral-codes`),

  // Generate sample referral codes
  generateSample: (count = 100) => api.post('/referral-codes/generate-sample', { count }),

  // Search referral codes
  search: (params = {}) => api.get('/referral-codes/codes', { params }),

  // Export referral codes
  exportData: (params = {}) => api.get('/referral-codes/export', { params, responseType: 'blob' }),
};

// Generation Reports API
export const generationReportsAPI = {
  getPerformanceSummary: (params = {}) => api.get('/generation-reports/performance-summary', { params }),
  getFailureBreakdown: (params = {}) => api.get('/generation-reports/failure-breakdown', { params }),
  getDailyTrend: (params = {}) => api.get('/generation-reports/daily-trend', { params }),
  exportCSV: (params = {}) => api.get('/generation-reports/export-csv', { params, responseType: 'blob' }),
  exportSummaryCSV: (params = {}) => api.get('/generation-reports/export-daily-summary', { params, responseType: 'blob' }),
  getMetabaseEmbedUrl: (params = {}) => api.get('/generation-reports/metabase-embed-url', { params }),
};

// Health API
export const healthAPI = {
  check: () => api.get('/health'),
};

// AI Generation API
export const aiGenerationAPI = {
  // New endpoint to get bust colors filtered by bust finish
  getBustColorsByFinish: (bustFinishId) => api.get(`/ai-generation/bust-colors-by-finish/${bustFinishId}`),
};

// Feedback Management API
export const feedbackAPI = {
  getAll: (params = {}) => api.get('/feedback/', { params }),
  getById: (id) => api.get(`/feedback/${id}`),
  processRefund: (feedbackId, data) => api.post(`/feedback/${feedbackId}/refund`, data),
  getStats: () => api.get('/feedback/stats'),
  exportData: (params = {}) => api.get('/feedback/export', { params, responseType: 'blob' }),
};

// Contact Enquiries Management API
export const contactEnquiriesAPI = {
  getAll: (params = {}) => api.get('/contact-enquiries/', { params }),
  updateStatus: (id, data) => api.put(`/contact-enquiries/${id}/status`, data),
  delete: (id) => api.delete(`/contact-enquiries/${id}`),
  exportData: (params = {}) => api.get('/contact-enquiries/export', { params, responseType: 'blob' }),
};

export default api; 